import {
  Component, OnInit, Input, OnDestroy, OnChanges
} from '@angular/core';

import {
  FinancialArea,
  FinancialTypeWithData,
  FinancialType,
  FinancialData,
  // Message,
  CurrencyConversion,
  FinancialDataModalPayload
} from '../../../shared/models';

import { FinancialDataService } from '../../../shared/services/financial-data.service';
import { EntitiesService, NotificationService, NumberFormatService } from '../../../shared/services';
import { ModalService } from '../../../shared/modules/modal';

import { ComponentWithErrorModal } from '../../../shared/components/base/component-with-error-modal';

// import { MessageModalComponent } from '../../../shared/components/message/message-modal.component';
import { FinancialsDataModalComponent } from './financials-data-modal/financials-data-modal.component';

// import { TranslationConstants } from '../../../shared/constants/translation';

import { FinancialDataObjectTypeEnum } from '../../../shared/models/enums';
import { FinancialSectionConstants, TinyTextEditor } from '../../../shared/constants';

@Component({
  selector: 'financials-table',
  templateUrl: './financials-table.component.html',
})
export class FinancialsTableComponent extends ComponentWithErrorModal implements OnInit, OnDestroy, OnChanges {
  @Input() financialArea = new FinancialArea();
  @Input() section: string;
  @Input() taxYear: number;
  @Input() objectId: number;
  @Input() objectType: FinancialDataObjectTypeEnum;
  @Input() objectCurrencyConversion: CurrencyConversion[];
  @Input() numberOfYears = 5;
  @Input() primaryHeaderValue: string;
  @Input() secondaryHeaderValue: string;
  @Input() entityCountryId: number;

  typesWithData: FinancialTypeWithData[] = [];
  taxYears: number[];
  idPropertyName = '';
  dropdownLabelTranslationKey = '';
  tinyInit = TinyTextEditor.INIT;
  additionalFinancial: string;
  shouldDisplayFinancial: boolean = false;
  isEntityFinancial: boolean = false;
  isTestedPartyFinancial: boolean = false;
  financialSectionConstants = FinancialSectionConstants;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _financialDataService: FinancialDataService,
    private _numberFormatService: NumberFormatService,
    private _entitiesService: EntitiesService
  ) {
    super(notificationService, modalService);
  }

  ngOnInit(): void {
    const financialAdditionalInfoSections = [FinancialSectionConstants.ENTITY_INCOME_STATEMENT, FinancialSectionConstants.PBA_INCOME_STATEMENT];
    this.shouldDisplayFinancial = financialAdditionalInfoSections.includes(this.section);
    if (this.shouldDisplayFinancial) {
      this.section === FinancialSectionConstants.ENTITY_INCOME_STATEMENT ? this.isEntityFinancial = true : this.isTestedPartyFinancial = true;
    }
    this.listenForErrorMessages();
    this.initFinancialData();
    this.getAdditionalFinancial();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  ngOnChanges(changes): void {
    if (changes.financialArea && changes.financialArea.currentValue) {
      this.initFinancialData();
      this.setDropdownInstructionsTranslationKey();
    }
  }

  saveFinancial(): void {
    const shouldSendFinancialData = typeof this.additionalFinancial === 'string';
    const payload = { financialsInfo: this.additionalFinancial };
    if (shouldSendFinancialData && this.section === FinancialSectionConstants.ENTITY_INCOME_STATEMENT) {
      this._entitiesService.saveEntityFinancialsInfo(this.objectId, payload).subscribe(() => {});
    }
    if (shouldSendFinancialData && this.section === FinancialSectionConstants.PBA_INCOME_STATEMENT) {
      this._entitiesService.saveTestedPartiesFinancialsInfo(this.objectId, payload).subscribe(() => {});
    }
  }

  getAdditionalFinancial(): void {
    if (this.section === FinancialSectionConstants.ENTITY_INCOME_STATEMENT) {
      this._entitiesService.getEntityFinancialsInfo(this.objectId).subscribe(data => {
        this.additionalFinancial = data.data;
      });
    }
    if (this.section === FinancialSectionConstants.PBA_INCOME_STATEMENT) {
      this._entitiesService.getTestedPartyFinancialsInfo(this.objectId).subscribe(data => {
        this.additionalFinancial = data.data;
      });
    }
  }

  getFormated(num, maxFractionDigits = 1): string {
    if (num === '.') {
      return '0.';
    }
    if (!num) {
      return '';
    }

    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits, 2);
  }

  openInputDataModal(taxYear: number): void {
    const dataForTaxYear = this.typesWithData.map((type) => {
      return type.financialData.find(financial => financial.taxYear === taxYear);
    });

    this.isModalOpen = true;
    this.modalService.openModal({
      component: FinancialsDataModalComponent,
      data: {
        taxYear,
        typesWithData: dataForTaxYear,
        objectId: this.objectId,
        financialArea: this.financialArea,
        section: this.section,
        objectType: this.objectType,
        objectCurrencyConversion: this.objectCurrencyConversion,
        primaryHeaderValue: this.primaryHeaderValue,
        secondaryHeaderValue: this.secondaryHeaderValue,
        entityCountryId: this.entityCountryId,
      } as FinancialDataModalPayload,
      success: () => {
        this.typesWithData = [];
        this.initFinancialData();
        this.isModalOpen = false;
      },
    });
  }
  getColumnWidth(): string {
    return `${80 / this.taxYears.length || 1}%`;
  }

  private setDropdownInstructionsTranslationKey(): void {
    const area = this.financialArea.name.split('.')[1];

    this.dropdownLabelTranslationKey = `financial_area.input_${area}_for`;
  }

  private getFinancialData(): void {
    this._financialDataService.getFinancialData(
      this.objectId, this.financialArea.financialAreaId, this.objectType)
      .subscribe(({ data }) => {
        if (data) {
          data.map((financialData: FinancialData) => {
            const foundType = this.typesWithData.find((financialTypeWithData: FinancialTypeWithData) => {
              return financialTypeWithData.financialTypeId === financialData.financialType.financialTypeId;
            });
            if (foundType) {
              const efData = foundType.financialData.find((efd: FinancialData) => {
                return financialData.taxYear === efd.taxYear;
              });
              if (efData) {
                Object.assign(efData, financialData);
              }
            }
          });
        }
      });
  }

  private initFinancialData(): void {
    if (!this.financialArea) {
      return;
    }

    this.taxYears = [];
    for (let index = this.taxYear; index > this.taxYear - this.numberOfYears; index--) {
      this.taxYears.push(index);
    }
    this.idPropertyName = this.getIdPropertyName();
    if (this.typesWithData.length === 0 && this.objectId) {
      this.typesWithData = this.financialArea.financialTypes.map((financialType: FinancialType) => {
        const typeWithData = new FinancialTypeWithData();
        Object.assign(typeWithData, financialType);
        typeWithData.financialData = [];
        this.taxYears.forEach((year) => {
          const efd = new FinancialData();
          efd.taxYear = year;
          efd.financialType = financialType;
          typeWithData.financialData.push(efd);
        });

        return typeWithData;
      });

      this.getFinancialData();
    }
  }

  // protected handleErrors(response: any): void {
  //   if (response.status === 422) {
  //     this.modalService.openModal({
  //       component: MessageModalComponent,
  //       data: {
  //         title: TranslationConstants.GLOBAL_ERROR,
  //         content: TranslationConstants.GLOBAL_SERVER_ERROR,
  //       } as Message,
  //     });
  //     return;
  //   }
  //   // handle all other errors
  //   super.handleErrors(response);
  // }

  protected getIdPropertyName(): string {
    switch (this.objectType) {
      case FinancialDataObjectTypeEnum.ENTITY:
        return 'entityFinancialDataId';
      case FinancialDataObjectTypeEnum.MNE:
        return 'mneFinancialDataId';
      case FinancialDataObjectTypeEnum.TESTED_PARTY:
        return 'testedPartyFinancialDataId';
      default:
        return '';
    }
  }
}
